import React, { useState } from "react";
import { api } from "@/lib/api";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Multilingual text input with auto-translate (EN → TR/ID).
 * value: { en, tr, id }
 * onChange: (newValue) => void
 */
const MultilingualField = ({ label, value, onChange, multiline = false, required = false, testId = "ml-field" }) => {
  const [active, setActive] = useState("en");
  const [loading, setLoading] = useState(false);

  const v = value || { en: "", tr: "", id: "" };

  const update = (lang, text) => onChange({ ...v, [lang]: text });

  const autoTranslate = async () => {
    const source = active;
    const text = v[source];
    if (!text?.trim()) {
      toast.error(`Enter text in ${source.toUpperCase()} first`);
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/translate", { text, source });
      onChange({ en: res.data.en || v.en, tr: res.data.tr || v.tr, id: res.data.id || v.id });
      toast.success("Translated to all languages");
    } catch (e) {
      toast.error("Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const Tag = multiline ? "textarea" : "input";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}{required && " *"}</label>
        <div className="flex items-center gap-1">
          {["en", "tr", "id"].map((l) => (
            <button
              key={l} type="button"
              data-testid={`${testId}-tab-${l}`}
              onClick={() => setActive(l)}
              className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm font-semibold ${
                active === l ? "bg-[#0A1128] text-[#D4AF37]" : "text-slate-400 hover:text-[#0A1128]"
              }`}
            >{l}</button>
          ))}
          <button
            type="button" onClick={autoTranslate} disabled={loading}
            data-testid={`${testId}-translate`}
            className="ml-2 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0A1128] font-semibold flex items-center gap-1 disabled:opacity-50"
            title="Auto-translate"
          >
            {loading ? <Loader2 size={10} className="animate-spin" /> : <Languages size={10} />}
            AI
          </button>
        </div>
      </div>
      <Tag
        value={v[active] || ""}
        onChange={(e) => update(active, e.target.value)}
        rows={multiline ? 4 : undefined}
        required={required && active === "en"}
        data-testid={`${testId}-input`}
        className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
      />
    </div>
  );
};

export default MultilingualField;
